/**
 * F-074 — Texas email sequence builder & Brevo preview (E-022 FE, Batch-8).
 *
 * Shape source: LP src/lib/email-cadence.js — the five segment cadences are a
 * 7-touch cold -> demo -> proposal -> follow-up-1..4 sequence at
 * 0/3/7/11/15/19/23 days, each step carrying { key, offsetDays, subject,
 * heading, points }. That step object is `brevoTemplateShape` as the batch-8
 * assign describes it; validateBrevoTemplateShape() enforces it on the touch
 * templates below.
 *
 * LP exposes no cadence HTTP route (the engine is lib-only, driven by cron),
 * so the preview is rendered FE-side and the Brevo-rendered HTML binds to the
 * nominated path EMAIL_SEQUENCE_PATHS.preview, degrading to null when buffy's
 * batch-8 BE contract is not yet published.
 */
import { landlordApi } from './api';

export type SequenceTouchKey =
    | 'cold'
    | 'demo'
    | 'proposal'
    | 'follow-up-1'
    | 'follow-up-2'
    | 'follow-up-3'
    | 'follow-up-4';

export const SEQUENCE_TOUCH_ORDER: SequenceTouchKey[] = [
    'cold',
    'demo',
    'proposal',
    'follow-up-1',
    'follow-up-2',
    'follow-up-3',
    'follow-up-4',
];

export const TOUCH_OFFSET_DAYS: Record<SequenceTouchKey, number> = {
    cold: 0,
    demo: 3,
    proposal: 7,
    'follow-up-1': 11,
    'follow-up-2': 15,
    'follow-up-3': 19,
    'follow-up-4': 23,
};

export interface SequenceTouch {
    key: SequenceTouchKey;
    offsetDays: number;
    subject: string;
    heading: string;
    points: string[];
    topicId?: string;
}

export interface BrevoTemplateShape {
    subject: string;
    heading: string;
    points: string[];
    preheader?: string;
    fromName?: string;
}

export const BREVO_TEMPLATE_RULES = {
    maxSubject: 120,
    maxPreheader: 140,
    minPoints: 1,
    maxPoints: 6,
};

export function validateBrevoTemplateShape(template: Partial<BrevoTemplateShape>): string[] {
    const errors: string[] = [];
    const subject = String(template.subject || '');
    const heading = String(template.heading || '');
    const points = Array.isArray(template.points) ? template.points : [];

    if (!subject.trim()) errors.push('subject is required');
    else if (subject.length > BREVO_TEMPLATE_RULES.maxSubject) errors.push('subject exceeds 120 characters');
    if (!heading.trim()) errors.push('heading is required');
    if (points.length < BREVO_TEMPLATE_RULES.minPoints) errors.push('at least one point is required');
    if (points.length > BREVO_TEMPLATE_RULES.maxPoints) errors.push('more than six points');
    if (template.preheader && template.preheader.length > BREVO_TEMPLATE_RULES.maxPreheader) {
        errors.push('preheader exceeds 140 characters');
    }
    return errors;
}

export interface EmailTopic {
    id: string;
    label: string;
    jurisdiction: string;
    angle: string;
    points: string[];
}

export const TX_EMAIL_TOPICS: EmailTopic[] = [
    {
        id: 'sb38',
        label: 'SB 38 compliance guide',
        jurisdiction: 'Texas',
        angle: 'Eviction-notice timing under SB 38',
        points: [
            'SB 38 notice windows tracked per tenancy',
            'Non-delegable owner obligations stay visible',
            'Evidence pack retained for every notice served',
        ],
    },
    {
        id: 'houston-ch10',
        label: 'Houston Ch. 10 ASEC',
        jurisdiction: 'City of Houston',
        angle: 'Proactive inspection readiness under Ch. 10 Art. II',
        points: [
            'Annual Self-Inspection Certification (ASEC) filed on time',
            'Ch. 10 Art. II deficiencies closed before the deadline',
            'Registration status reconciled with the Houston portal',
        ],
    },
    {
        id: 'dallas-ch27',
        label: 'Dallas Ch. 27 registration',
        jurisdiction: 'City of Dallas',
        angle: 'Annual rental registration and renewal',
        points: [
            'Dallas Ch. 27 annual registration in one workflow',
            'Renewal deadlines surfaced before the fee window',
            'Portfolio-wide registration posture per property',
        ],
    },
    {
        id: 'us-residency',
        label: 'US data residency',
        jurisdiction: 'United States',
        angle: 'Where Texas tenancy data is stored and processed',
        points: [
            'US data residency for Texas tenancies',
            'No cross-border processing of tenant records',
            'Audit trail of who accessed what, and when',
        ],
    },
];

const DEFAULT_SEQUENCE: Array<Omit<SequenceTouch, 'topicId'>> = [
    {
        key: 'cold',
        offsetDays: TOUCH_OFFSET_DAYS.cold,
        subject: 'Who owns the Texas deadline when your team is off site?',
        heading: 'Compliance does not take a night off',
        points: [
            'SB 38, Houston Ch. 10 and Dallas Ch. 27 in one engine',
            'Non-delegable obligations stay visible to ownership',
            'US data residency for Texas tenancies',
        ],
    },
    {
        key: 'demo',
        offsetDays: TOUCH_OFFSET_DAYS.demo,
        subject: 'See your portfolio scored against the Texas rules',
        heading: 'A 20-minute walkthrough of your own properties',
        points: [
            'Score every property against SB 38 and municipal rules',
            'Subchapter D gaps surfaced before inspection',
            'Export the same evidence pack your auditor asks for',
        ],
    },
    {
        key: 'proposal',
        offsetDays: TOUCH_OFFSET_DAYS.proposal,
        subject: 'Pricing that scales with units, not per filing',
        heading: 'Predictable per-unit pricing',
        points: [
            'Ascending per-unit tiers — pay for what you hold',
            'Dallas + Houston rules in one engine',
            'US data residency for Texas tenancies',
        ],
    },
    {
        key: 'follow-up-1',
        offsetDays: TOUCH_OFFSET_DAYS['follow-up-1'],
        subject: 'The Houston Ch. 10 ASEC deadline most owners miss',
        heading: 'Proactive inspection readiness',
        points: [
            'Annual Self-Inspection Certification filed on schedule',
            'Ch. 10 Art. II deficiencies closed before the window',
            'Evidence retained with every certification',
        ],
    },
    {
        key: 'follow-up-2',
        offsetDays: TOUCH_OFFSET_DAYS['follow-up-2'],
        subject: 'Dallas Ch. 27 renewals without the spreadsheet',
        heading: 'Registration that renews itself',
        points: [
            'Annual registration tracked per property',
            'Renewal reminders before the fee window',
            'Portfolio registration posture at a glance',
        ],
    },
    {
        key: 'follow-up-3',
        offsetDays: TOUCH_OFFSET_DAYS['follow-up-3'],
        subject: 'What subchapter D asks for that you are not tracking',
        heading: 'Close the gaps before the inspection',
        points: [
            'Subchapter D requirement gaps ranked by risk',
            'Owner-certified remediation trail',
            'One evidence pack per property',
        ],
    },
    {
        key: 'follow-up-4',
        offsetDays: TOUCH_OFFSET_DAYS['follow-up-4'],
        subject: 'Ready to move your Texas portfolio onto the engine?',
        heading: 'Start with one property',
        points: [
            'No migration project — first property live in a day',
            'US data residency from day one',
            'Cancel any time during the first month',
        ],
    },
];

export const SEQUENCE_ID = 'seg-texas-marketing';

export function buildSequence(topicId?: string): SequenceTouch[] {
    const topic = TX_EMAIL_TOPICS.find((t) => t.id === topicId);
    return DEFAULT_SEQUENCE.map((touch) => ({ ...touch, points: [...touch.points], topicId: topic?.id }));
}

export function topicFor(touch: SequenceTouch): EmailTopic | null {
    if (!touch.topicId) return null;
    return TX_EMAIL_TOPICS.find((t) => t.id === touch.topicId) || null;
}

export interface TouchPreview {
    subject: string;
    heading: string;
    points: string[];
    topic: EmailTopic | null;
    utm: string;
    validationErrors: string[];
}

export function previewTouch(touch: SequenceTouch, topicId?: string, baseUrl = 'https://compliance-engine.io/texas/demo'): TouchPreview {
    const topic = topicId ? TX_EMAIL_TOPICS.find((t) => t.id === topicId) || null : topicFor(touch);
    const points = topic ? [...topic.points, ...touch.points].slice(0, BREVO_TEMPLATE_RULES.maxPoints) : touch.points;
    const template: BrevoTemplateShape = { subject: touch.subject, heading: touch.heading, points };
    return {
        subject: touch.subject,
        heading: touch.heading,
        points,
        topic,
        utm: buildUtmPreview(baseUrl, { source: 'brevo', medium: 'email', campaign: `tx-cadence-${touch.key}` }),
        validationErrors: validateBrevoTemplateShape(template),
    };
}

export function buildUtmPreview(
    url: string,
    params: { source: string; medium: string; campaign: string; content?: string },
): string {
    const separator = url.includes('?') ? '&' : '?';
    const query = [
        `utm_source=${encodeURIComponent(params.source)}`,
        `utm_medium=${encodeURIComponent(params.medium)}`,
        `utm_campaign=${encodeURIComponent(params.campaign)}`,
    ];
    if (params.content) query.push(`utm_content=${encodeURIComponent(params.content)}`);
    return `${url}${separator}${query.join('&')}`;
}

export const EMAIL_SEQUENCE_PATHS = {
    preview: 'api/email-cadence/preview',
} as const;

const unwrapBody = (res: any): any => {
    const body = res?.data;
    if (body && body.success === false) return null;
    return body && body.data !== undefined ? body.data : body;
};

export const emailSequenceApi = {
    /**
     * Brevo-rendered HTML for one touch. Nominated contract — returns null
     * while the batch-8 cadence BE is unpublished so the caller falls back to
     * the FE-side preview rendered by previewTouch().
     */
    brevoPreview: (cadenceId: string, touchKey: SequenceTouchKey): Promise<string | null> =>
        landlordApi
            .post(EMAIL_SEQUENCE_PATHS.preview, { cadenceId, touch: touchKey })
            .then((res: any) => {
                const data = unwrapBody(res);
                if (typeof data === 'string') return data;
                if (data && typeof data.html === 'string') return data.html;
                return null;
            })
            .catch(() => null),
};

export default emailSequenceApi;
